import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

/**
 * Global 503 `coming_soon_mode` interceptor.
 *
 * When Coming Soon Mode is ON, the backend rejects non-admin API calls with
 * HTTP 503 + { detail: "coming_soon_mode" }. If a stale-session logged-in
 * non-admin user encounters that on a protected page, force a hard reload
 * to `/` so LaunchGate can render the waitlist page instead.
 */
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    try {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (
        status === 503 &&
        detail === "coming_soon_mode" &&
        typeof window !== "undefined" &&
        window.location.pathname !== "/" &&
        !window.location.pathname.startsWith("/login") &&
        !window.location.pathname.startsWith("/register") &&
        !window.location.pathname.startsWith("/verify-email")
      ) {
        window.location.replace("/");
      }
    } catch (_) { /* no-op */ }
    return Promise.reject(err);
  }
);

/**
 * Build a clear, user-facing message from anything axios may throw.
 * Handles:
 *  - structured FastAPI `detail` payloads (string, list of {msg}, or object)
 *  - network failures (CORS, DNS, offline, mixed content) — these arrive with
 *    NO `error.response` and `error.message === "Network Error"`
 *  - explicit HTTP status codes (429 rate limit, 5xx server error, etc.)
 */
export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * After a successful login/register, immediately verify the auth cookie was
 * actually accepted by the browser. Safari's Intelligent Tracking Prevention
 * and Chrome's strict third-party-cookie mode will silently drop our cookies
 * if the frontend and backend are on different origins — the POST succeeds,
 * the response has Set-Cookie, but the next request goes out without it.
 *
 * Returns true if the session is persisted, false if the cookie was dropped.
 */
export async function verifySessionPersisted() {
  try {
    const { status } = await api.get("/api/auth/me", { validateStatus: () => true });
    return status >= 200 && status < 300;
  } catch (e) {
    return false;
  }
}

/**
 * Single helper used by every auth/signup/login form so error messages are
 * always specific. Never returns the dreaded "Something went wrong".
 */
export function formatAuthError(err, fallback = "Sign-in failed. Please try again.") {
  if (!err) return fallback;
  const resp = err.response;

  // 1) The backend responded with a structured error
  if (resp && resp.data) {
    const detail = formatApiErrorDetail(resp.data.detail);
    if (detail) return detail;
    if (typeof resp.data === "string" && resp.data.trim()) return resp.data;
  }

  // 2) The backend responded but with no body (rare) — use status text
  if (resp) {
    if (resp.status === 401) return "Invalid email or password.";
    if (resp.status === 403) return "Access denied. Please contact support.";
    if (resp.status === 404) return "Account not found. Please sign up first.";
    if (resp.status === 409) return "An account with this email already exists.";
    if (resp.status === 429) return "Too many attempts. Please wait a minute and try again.";
    if (resp.status >= 500) return "Our server hit a snag. Please try again in a moment.";
    return resp.statusText || fallback;
  }

  // 3) No response at all → network / CORS / DNS / offline / mixed-content
  // axios sets err.message === "Network Error" for these.
  const msg = (err.message || "").toLowerCase();
  if (msg.includes("network") || err.code === "ERR_NETWORK") {
    return "Couldn't reach FlowFinder servers. Check your connection or try again in a moment.";
  }
  if (err.code === "ECONNABORTED" || msg.includes("timeout")) {
    return "The request timed out. Please try again.";
  }
  return err.message || fallback;
}
